<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed } from "vue"
import { UiButton, UiCepInput, UiCnpjInput, UiCpfInput, UiFormLayout, UiPhoneInput, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type AddressPayload = {
  id?: number | null
  label: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  zip_code: string
  _destroy?: boolean
}

type PostalCodeAddress = {
  zip_code: string
  street: string
  district: string
  city: string
  state: string
}

type CustomerPayload = {
  id?: number | null
  company_id: number | ""
  name: string
  person_type: string
  document: string
  email: string
  phone: string
  addresses: AddressPayload[]
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  customer: CustomerPayload
  companies: Array<{ id: number; name: string }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  customer: {
    company_id: props.customer.company_id,
    name: props.customer.name,
    person_type: props.customer.person_type,
    document: props.customer.document,
    email: props.customer.email,
    phone: props.customer.phone,
    addresses_attributes: props.customer.addresses.map((address) => ({ ...address, _destroy: false })),
  },
})

const notify = useNotifications()

const companyOptions = computed(() => props.companies.map((company) => ({ label: company.name, value: company.id })))
const personTypeOptions = [
  { label: "Pessoa fisica", value: "individual" },
  { label: "Pessoa juridica", value: "company" },
]
const visibleAddresses = computed(() => form.customer.addresses_attributes.filter((address) => !address._destroy))
const fieldError = (field: string) => props.errors?.[field]?.[0]

const isIndividual = computed(() => form.customer.person_type === "individual")

function addressError(index: number, field: string) {
  return props.errors?.[`customer_addresses.${index}.${field}`]?.[0] || props.errors?.[`addresses_attributes.${index}.${field}`]?.[0]
}

function addAddress() {
  form.customer.addresses_attributes.push({
    id: null,
    label: "entrega",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    zip_code: "",
    _destroy: false,
  })
}

function removeAddress(index: number) {
  const address = form.customer.addresses_attributes[index]
  if (!address) return

  if (address.id) {
    address._destroy = true
    return
  }

  form.customer.addresses_attributes.splice(index, 1)
}

function applyAddressLookup(index: number, payload: PostalCodeAddress) {
  const address = form.customer.addresses_attributes[index]
  if (!address) return

  address.zip_code = payload.zip_code || address.zip_code
  address.street = payload.street || ""
  address.district = payload.district || ""
  address.city = payload.city || ""
  address.state = payload.state || ""
}

function onLookupError(message: string) {
  notify.warning(message)
}

function submit() {
  const options = {
    onSuccess: () => notify.success(props.mode === "create" ? "Cliente criado com sucesso" : "Cliente atualizado com sucesso"),
  }

  if (props.mode === "create") {
    form.post("/customers", options)
    return
  }

  form.put(`/customers/${props.customer.id}`, options)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/customers" back-label="Voltar para clientes">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-3">
        <UiSelect
          id="customer-company"
          :model-value="form.customer.company_id"
          label="Empresa"
          :options="companyOptions"
          :disabled="mode === 'edit'"
          :error="fieldError('company') || fieldError('company_id')"
          @update:model-value="form.customer.company_id = $event as number | ''"
        />

        <UiSelect
          id="customer-person-type"
          :model-value="form.customer.person_type"
          label="Tipo de pessoa"
          :options="personTypeOptions"
          :error="fieldError('person_type')"
          @update:model-value="form.customer.person_type = String($event || 'individual')"
        />

        <UiTextInput id="customer-name" v-model="form.customer.name" label="Nome" :error="fieldError('name')" />

        <UiCpfInput
          v-if="isIndividual"
          id="customer-document-cpf"
          v-model="form.customer.document"
          label="CPF"
          :error="fieldError('document')"
        />
        <UiCnpjInput
          v-else
          id="customer-document-cnpj"
          v-model="form.customer.document"
          label="CNPJ"
          :error="fieldError('document')"
        />
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <UiTextInput id="customer-email" v-model="form.customer.email" type="email" label="Email" :error="fieldError('email')" />
        <UiPhoneInput id="customer-phone" v-model="form.customer.phone" label="Telefone" :error="fieldError('phone')" />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-slate-800">Enderecos</h2>
          <UiButton type="button" size="sm" variant="secondary" @click="addAddress">Adicionar endereco</UiButton>
        </div>

        <div class="space-y-3">
          <article
            v-for="(address, index) in form.customer.addresses_attributes"
            v-show="!address._destroy"
            :key="index"
            class="rounded-md border border-slate-100 p-3"
          >
            <div class="grid gap-3 md:grid-cols-4">
              <UiCepInput
                :id="`customer-address-zip-${index}`"
                v-model="address.zip_code"
                label="CEP"
                helper-text="Informe o CEP para buscar endereco automaticamente"
                :error="addressError(index, 'zip_code')"
                @lookup-success="applyAddressLookup(index, $event)"
                @lookup-error="onLookupError"
              />

              <UiSelect
                :id="`customer-address-label-${index}`"
                :model-value="address.label"
                label="Tipo"
                :options="[
                  { label: 'Principal', value: 'principal' },
                  { label: 'Entrega', value: 'entrega' },
                  { label: 'Faturamento', value: 'faturamento' },
                ]"
                :error="addressError(index, 'label')"
                @update:model-value="address.label = String($event || '')"
              />
              <UiTextInput :id="`customer-address-street-${index}`" v-model="address.street" label="Rua" :error="addressError(index, 'street')" />
              <UiTextInput :id="`customer-address-number-${index}`" v-model="address.number" label="Numero" :error="addressError(index, 'number')" />
            </div>

            <div class="mt-3 grid gap-3 md:grid-cols-4">
              <UiTextInput :id="`customer-address-complement-${index}`" v-model="address.complement" label="Complemento" :error="addressError(index, 'complement')" />
              <UiTextInput :id="`customer-address-district-${index}`" v-model="address.district" label="Bairro" :error="addressError(index, 'district')" />
              <UiTextInput :id="`customer-address-city-${index}`" v-model="address.city" label="Cidade" :error="addressError(index, 'city')" />
              <UiTextInput :id="`customer-address-state-${index}`" v-model="address.state" label="UF" :error="addressError(index, 'state')" />
            </div>

            <div class="mt-3 flex justify-end">
              <UiButton type="button" size="sm" variant="danger" @click="removeAddress(index)">Remover endereco</UiButton>
            </div>
          </article>

          <p v-if="visibleAddresses.length === 0" class="text-sm text-slate-500">Adicione ao menos um endereco para o cliente.</p>
          <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>
        </div>
      </section>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar cliente" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/customers" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>

